/**
 * Printing with no connection: the kitchen's ticket and the guest's receipt.
 *
 * A network printer (Epson, Star) is sent text, as it is online — it is on the
 * restaurant's network, so it does not need the internet. A tablet set to its
 * own printer prints through the browser: the lines are put in a hidden frame
 * and that frame is printed, so nothing else on the screen comes out with them.
 */

import type { PrintLine } from '@restaurant/ui';

import { isTillApp, tillApp } from '../native';
import { loadPrinter, PrinterError, sendToPrinter } from '../printer';

function escapeHtml(text: string): string {
  return text.replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!,
  );
}

/** The lines as a page of their own, monospaced as the printer would set them. */
function pageOf(lines: PrintLine[]): string {
  const body = lines
    .map((line) => {
      const style = [
        line.align === 'center' ? 'text-align:center' : '',
        line.bold ? 'font-weight:700' : '',
        line.big ? 'font-size:1.6em' : '',
      ]
        .filter(Boolean)
        .join(';');
      return `<div style="${style}">${escapeHtml(line.text) || '&nbsp;'}</div>`;
    })
    .join('');
  return `<!doctype html><html><head><title>Print</title><style>
      @page { margin: 4mm; }
      body { font: 12px/1.35 ui-monospace, Menlo, Consolas, monospace; white-space: pre; margin: 0; }
    </style></head><body>${body}</body></html>`;
}

function printInFrame(lines: PrintLine[]): void {
  // A web view has no print window for a frame: the till app prints the page itself.
  if (isTillApp()) {
    void tillApp.printHtml(pageOf(lines)).catch(() => undefined);
    return;
  }
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;width:0;height:0;border:0;right:0;bottom:0';
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc) return;
  doc.open();
  doc.write(pageOf(lines));
  doc.close();
  frame.contentWindow?.focus();
  frame.contentWindow?.print();
  window.setTimeout(() => frame.remove(), 60_000);
}

/** Print these lines on this tablet's printer. Returns a problem in words, or null. */
export async function printLines(
  lines: PrintLine[],
  { cut = true } = {},
): Promise<string | null> {
  const settings = loadPrinter();
  if (settings.kind === 'browser') {
    printInFrame(lines);
    return null;
  }
  try {
    await sendToPrinter(settings, { lines, cut, drawer: false });
    return null;
  } catch (e) {
    return e instanceof PrinterError ? e.message : 'It did not print.';
  }
}

/** Characters per line on this tablet's printer. */
export function printWidth(): number {
  return loadPrinter().width;
}
